import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { stepsForPage, TOUR_STEPS, type TourStep } from "./tourSteps";

interface OnboardingTourProps {
  open: boolean;
  /** Called whenever the user dismisses the tour mid-way (skip / close / ESC). */
  onSkip: () => void;
  /** Called when the user finishes the final step. */
  onComplete: () => void;
}

const SPOTLIGHT_PADDING = 8;
const BUBBLE_GAP = 16;
const BUBBLE_WIDTH = 320;

const isMobileViewport = () =>
  typeof window !== "undefined" && window.innerWidth < 768;

/** The first match that's actually on screen (skips hidden duplicates). */
function visibleTarget(selector: string): HTMLElement | null {
  let chosen: HTMLElement | null = null;
  document.querySelectorAll<HTMLElement>(selector).forEach((el) => {
    if (chosen) return;
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) chosen = el;
  });
  return chosen;
}

// The tour only points at things already on screen and never navigates, so
// the page the owner was on (and anything unsaved on it) is untouched. While
// it's open, a transparent shield catches clicks so the page can't change
// underneath it.
const OnboardingTour = ({ open, onSkip, onComplete }: OnboardingTourProps) => {
  const [steps, setSteps] = useState<TourStep[]>(TOUR_STEPS);
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [isMobile, setIsMobile] = useState(isMobileViewport());
  const primaryRef = useRef<HTMLButtonElement | null>(null);

  const step: TourStep | undefined = steps[stepIndex];
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === steps.length - 1;
  const spotlightPadding = step?.spotlightPadding ?? SPOTLIGHT_PADDING;

  // Fresh start each time it opens, with only the steps this page can show.
  useEffect(() => {
    if (!open) return;
    setSteps(stepsForPage(TOUR_STEPS, (selector) => !!visibleTarget(selector), isMobileViewport()));
    setStepIndex(0);
  }, [open]);

  // Track viewport breakpoint.
  useEffect(() => {
    if (!open) return;
    const onResize = () => setIsMobile(isMobileViewport());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [open]);

  // Locate the target element and keep its bounding rect fresh.
  useEffect(() => {
    if (!open || !step?.targetSelector) {
      setRect(null);
      return;
    }
    let cancelled = false;
    const updateRect = () => {
      if (cancelled) return;
      const chosen = visibleTarget(step.targetSelector!);
      setRect(chosen ? chosen.getBoundingClientRect() : null);
    };
    const first = visibleTarget(step.targetSelector);
    first?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    updateRect();
    // The sidebar can collapse or the window resize while the tour is open.
    const interval = window.setInterval(updateRect, 250);
    window.addEventListener("resize", updateRect);
    window.addEventListener("scroll", updateRect, true);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener("resize", updateRect);
      window.removeEventListener("scroll", updateRect, true);
    };
  }, [open, stepIndex, step?.targetSelector]);

  // Keyboard users land on the main button of each step.
  useEffect(() => {
    if (open) primaryRef.current?.focus();
  }, [open, stepIndex]);

  const handleNext = useCallback(() => {
    if (isLast) {
      onComplete();
    } else {
      setStepIndex((s) => s + 1);
    }
  }, [isLast, onComplete]);

  const handleBack = useCallback(() => {
    setStepIndex((s) => Math.max(0, s - 1));
  }, []);

  // Keyboard: ESC closes, ←/→ navigate.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onSkip();
      else if (e.key === "ArrowRight") handleNext();
      else if (e.key === "ArrowLeft") handleBack();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onSkip, handleNext, handleBack]);

  // Compute the bubble's position based on the target rect + chosen placement.
  const bubbleStyle = useMemo<React.CSSProperties>(() => {
    if (isMobile) {
      // Mobile: dock at the bottom, full width minus padding.
      return {
        position: "fixed",
        left: 16,
        right: 16,
        bottom: 16,
        maxWidth: "none",
      };
    }
    if (!rect) {
      // No anchor (welcome step) → center.
      return {
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: BUBBLE_WIDTH,
      };
    }
    const placement = step?.placement ?? "right";
    const base: React.CSSProperties = { position: "fixed", width: BUBBLE_WIDTH };
    switch (placement) {
      case "right":
        return {
          ...base,
          top: Math.max(16, rect.top + rect.height / 2 - 80),
          left: Math.min(
            window.innerWidth - BUBBLE_WIDTH - 16,
            rect.right + BUBBLE_GAP,
          ),
        };
      case "left":
        return {
          ...base,
          top: Math.max(16, rect.top + rect.height / 2 - 80),
          left: Math.max(16, rect.left - BUBBLE_WIDTH - BUBBLE_GAP),
        };
      case "bottom":
        return {
          ...base,
          top: rect.bottom + BUBBLE_GAP,
          left: Math.min(
            window.innerWidth - BUBBLE_WIDTH - 16,
            Math.max(16, rect.left + rect.width / 2 - BUBBLE_WIDTH / 2),
          ),
        };
      case "top":
      default:
        return {
          ...base,
          bottom: window.innerHeight - rect.top + BUBBLE_GAP,
          left: Math.min(
            window.innerWidth - BUBBLE_WIDTH - 16,
            Math.max(16, rect.left + rect.width / 2 - BUBBLE_WIDTH / 2),
          ),
        };
    }
  }, [isMobile, rect, step?.placement]);

  if (!open || !step) return null;

  return createPortal(
    <div>
      {/* Click shield: the page behind the tour can't be clicked, so it can't
          change while the owner reads. Clicking it does nothing. */}
      <div
        aria-hidden
        className="fixed inset-0"
        style={{ zIndex: 50 }}
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => e.stopPropagation()}
      />

      {/* Spotlight: the box-shadow technique creates the dimmed surround in one element. */}
      {rect && !isMobile ? (
        <div
          aria-hidden
          className="pointer-events-none fixed rounded-lg ring-2 ring-primary transition-all duration-150"
          style={{
            top: rect.top - spotlightPadding,
            left: rect.left - spotlightPadding,
            width: rect.width + spotlightPadding * 2,
            height: rect.height + spotlightPadding * 2,
            boxShadow: "0 0 0 9999px rgba(0,0,0,0.55)",
            zIndex: 50,
          }}
        />
      ) : (
        <div
          aria-hidden
          className="pointer-events-none fixed inset-0 bg-black/55"
          style={{ zIndex: 50 }}
        />
      )}

      {/* Bubble */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-tour-title"
        className="rounded-xl border border-border bg-card shadow-xl animate-in fade-in zoom-in-95 duration-200"
        style={{ ...bubbleStyle, zIndex: 51 }}
      >
        <div className="p-5">
          <div className="flex items-start justify-between gap-3 mb-2">
            <h3
              id="onboarding-tour-title"
              className="text-base font-semibold text-foreground leading-tight"
            >
              {step.title}
            </h3>
            <button
              type="button"
              onClick={onSkip}
              aria-label="Close tour"
              className="text-muted-foreground hover:text-foreground rounded-md p-1 -m-1 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed mb-5">
            {step.body}
          </p>
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-muted-foreground tabular-nums">
              {stepIndex + 1} of {steps.length}
            </span>
            <div className="flex items-center gap-2">
              {!isFirst && (
                <Button variant="ghost" size="sm" onClick={handleBack}>
                  Back
                </Button>
              )}
              {!isLast && (
                <Button variant="ghost" size="sm" onClick={onSkip}>
                  Skip
                </Button>
              )}
              <Button ref={primaryRef} size="sm" onClick={handleNext}>
                {isLast ? "Got it" : "Next"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default OnboardingTour;
